// Import statements grouped by external and internal dependencies
import { useCallback } from 'react';
import { fetchGraphUsers } from '../apis/graph';
import { fetchSharePointUser } from '../apis/sharePoint';

// Types
import { AuthContextProps } from '@/contexts/AuthContext';
import {
  SharePointDataHook,
  SharePointApiResponse,
  GraphApiResponse,
  SharePointUserProfile,
} from '../components/container/forms/types';

/**
 * Type definitions for PA (Personal Assistant) related data
 */
export type PAKey = 'twLegalPA' | 'twLegalPA2' | 'twLegalPA3';

export interface PAUserDetailWithKey extends GraphApiResponse {
  key: PAKey;
}

interface PAValue {
  key: PAKey;
  value: string;
}

interface RoomFloorValue {
  key: string;
  value: string;
}

interface SharePointDataResponse {
  paUserDetails: PAUserDetailWithKey[];
  roomFloorValues: RoomFloorValue[];
}

// Constants
const PA_KEYS: PAKey[] = ['twLegalPA', 'twLegalPA2', 'twLegalPA3'];
const ROOM_FLOOR_KEY = 'twRoomFloor';

/**
 * Custom hook for managing SharePoint data operations
 * @param getAccessToken - Function to retrieve access token
 * @returns Object containing SharePoint data fetching functions
 */
export const useSharePointData = (
  getAccessToken: AuthContextProps['getAccessToken']
): SharePointDataHook => {
  /**
   * Fetches PA user details from Graph API
   * @param email - Email address of the PA
   * @param key - PA key identifier
   * @returns Promise resolving to PA user details with key
   */
  const fetchPAUserDetails = async (
    email: string,
    key: PAKey
  ): Promise<PAUserDetailWithKey> => {
    const emptyResponse: PAUserDetailWithKey = {
      value: [],
      key,
      '@odata.context': '',
    };

    if (!email) return emptyResponse;

    try {
      const userDetails = await fetchGraphUsers({
        getAccessToken,
        searchValue: email,
      });

      if (userDetails.success) {
        return {
          ...userDetails.data,
          key,
        } as PAUserDetailWithKey;
      }

      return emptyResponse;
    } catch (error) {
      console.error(`Error fetching PA user details for ${email}:`, error);
      return emptyResponse;
    }
  };

  /**
   * Extracts PA values from SharePoint user data
   * @param userData - SharePoint user data
   * @returns Array of PA values with their keys
   */
  const extractPAValues = (userData: SharePointUserProfile): PAValue[] => {
    return PA_KEYS.map((key) => ({
      key,
      value: (userData[key] as string) || '',
    }));
  };

  /**
   * Extracts room/floor values from SharePoint user data
   * @param userData - SharePoint user data
   * @returns Array of room/floor values
   */
  const extractRoomFloorValues = (
    userData: SharePointUserProfile
  ): RoomFloorValue[] => {
    return Object.entries(userData)
      .filter(([key]) => key === ROOM_FLOOR_KEY)
      .filter(([, value]) => value)
      .map(([key, value]) => ({
        key,
        value: value as string,
      }));
  };

  /**
   * Fetches SharePoint data for a given email
   * @param email - Email address to fetch data for
   * @returns Promise resolving to SharePoint data response
   */
  const fetchSharePointData = useCallback(
    async (email: string): Promise<SharePointDataResponse | null> => {
      if (!email) return null;
      try {
        const sharePointUser = await fetchSharePointUser({
          getAccessToken,
          searchValue: email,
        });

        if (sharePointUser.success && sharePointUser.data) {
          const userProfileData = sharePointUser.data;
          const paValues = extractPAValues(userProfileData);
          const roomFloorValues = extractRoomFloorValues(userProfileData);

          if (!paValues || !roomFloorValues) return null;

          const paUserDetails = await Promise.all(
            paValues.map(async ({ key, value }) => {
              const paEmail = value.split('|').pop() || '';
              return await fetchPAUserDetails(paEmail, key);
            })
          );

          return {
            paUserDetails,
            roomFloorValues,
          };
        }

        return null;
      } catch (error) {
        console.error('Error fetching SharePoint data:', error);
        return null;
      }
    },
    [getAccessToken]
  );
  // TODO: Fix these type errors
  return {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-expect-error
    fetchSharePointData,
    fetchSharePointUser: async (props: {
      getAccessToken: AuthContextProps['getAccessToken'];
      searchValue: string;
    }): Promise<SharePointApiResponse> => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-expect-error
      return fetchSharePointUser(props);
    },
  };
};
